import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Button,
  Collapse,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { format, subMonths } from 'date-fns';

export interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  serviceTypes: string[];
}

interface DashboardFiltersProps {
  onFilterChange: (filters: FilterOptions) => void;
  serviceTypes: string[];
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({ onFilterChange, serviceTypes }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(subMonths(new Date(), 3));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange({
      startDate,
      endDate,
      serviceTypes: selectedServiceTypes
    });
  };

  const handleClearFilters = () => {
    setStartDate(subMonths(new Date(), 3));
    setEndDate(new Date());
    setSelectedServiceTypes([]);
    onFilterChange({
      startDate: subMonths(new Date(), 3),
      endDate: new Date(),
      serviceTypes: []
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterListIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Dashboard Filters</Typography>
          </Box>
          <Button 
            color="primary" 
            onClick={handleToggleFilters}
            size="small"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        <Collapse in={showFilters}>
          <Divider sx={{ my: 2 }} />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="dd/MM/yyyy"
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      size: 'small'
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          background: 'rgba(30, 41, 59, 0.95)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          '& .MuiPickersDay-root': {
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.dark'
                              }
                            }
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            color: 'rgba(255, 255, 255, 0.9)'
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'rgba(255, 255, 255, 0.7)'
                          },
                          '& .MuiPickersYear-yearButton': {
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main'
                            }
                          }
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="dd/MM/yyyy"
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      size: 'small'
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          background: 'rgba(30, 41, 59, 0.95)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          '& .MuiPickersDay-root': {
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.dark'
                              }
                            }
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            color: 'rgba(255, 255, 255, 0.9)'
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'rgba(255, 255, 255, 0.7)'
                          },
                          '& .MuiPickersYear-yearButton': {
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main'
                            }
                          }
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleApplyFilters}
                    size="small"
                  >
                    Apply Filters
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleClearFilters}
                    size="small"
                    startIcon={<ClearIcon />}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
          
          {startDate && endDate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing data from {format(startDate, 'MMM d, yyyy')} to {format(endDate, 'MMM d, yyyy')}
              </Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default DashboardFilters;
